// Times/Unidades contra o WootDash. Diferente do Chatwoot: Team é ligado a uma
// Unit (unidade regional) + UF, e tem um flag IsFallback (não existe conceito de
// "membro do time" pelo lado do time — atribuição é feita pelo lado do agente, ver
// wootdashAgents.ts). É a arquitetura do WootDash; a tela se adapta a ela.
import { WOOTDASH_BASE, wootdashAuthHeaders } from '@/api/wootdash'

export interface CpTeam {
  id: string
  unitId: string
  name: string
  uf: string | null
  isFallback: boolean
}

export interface CpUnit {
  id: string
  name: string
  /** Estado da unidade — decide quais feriados estaduais valem pra ela. */
  uf: string | null
}

async function get<T>(token: string, path: string, fallback: T): Promise<T> {
  try {
    const res = await fetch(`${WOOTDASH_BASE}${path}`, { headers: wootdashAuthHeaders(token) })
    if (!res.ok) return fallback
    return await res.json()
  } catch {
    return fallback
  }
}

export function listTeams(token: string): Promise<CpTeam[]> {
  return get(token, '/times', [])
}

export function listUnits(token: string): Promise<CpUnit[]> {
  return get(token, '/unidades', [])
}

export async function createTeam(token: string, data: { unidadeId: string; nome: string; uf?: string; isFallback?: boolean }) {
  const res = await fetch(`${WOOTDASH_BASE}/times`, {
    method: 'POST',
    headers: { ...wootdashAuthHeaders(token), 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  })
  return { ok: res.ok, status: res.status }
}

export async function updateTeam(token: string, id: string, data: { nome: string; uf?: string; isFallback?: boolean }) {
  const res = await fetch(`${WOOTDASH_BASE}/times/${id}`, {
    method: 'PUT',
    headers: { ...wootdashAuthHeaders(token), 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  })
  return { ok: res.ok, status: res.status }
}

// ── Página Times (a mesma do dashboard antigo) ───────────────────────────────────────────────────────────────

export interface TeamFlowRef { id: string; number: number; name: string }

/** Linha da tabela: contagens e os fluxos que o time atende (das categorias dos membros). */
export interface TeamOverview {
  id: string; name: string; unitId: string; unitName: string | null; uf: string | null; isFallback: boolean
  externalUnitId: number | null; externalUnitName: string | null; memberCount: number; fallbackAgentCount: number; flowIds: string[]
}

export interface TeamPerson { id: string; name: string; email: string; avatarUrl: string | null; status: string; isActive: boolean; categoryIds: string[] }
export interface ExternalUnitOption { id: number; municipality: string }

async function call(token: string, method: string, path: string, body?: unknown): Promise<{ ok: boolean; status: number; data: any }> {
  try {
    const res = await fetch(`${WOOTDASH_BASE}${path}`, {
      method,
      headers: { ...wootdashAuthHeaders(token), ...(body ? { 'Content-Type': 'application/json' } : {}) },
      body: body ? JSON.stringify(body) : undefined,
    })
    const data = res.status === 204 ? null : await res.json().catch(() => null)
    return { ok: res.ok, status: res.status, data }
  } catch {
    return { ok: false, status: 0, data: null }
  }
}

export async function getTeamsOverview(token: string): Promise<{ teams: TeamOverview[]; flows: TeamFlowRef[] } | null> {
  const r = await call(token, 'GET', '/times/painel')
  return r.ok ? r.data : null
}
export async function listTeamMembers(token: string, teamId: string): Promise<TeamPerson[] | null> {
  const r = await call(token, 'GET', `/times/${teamId}/membros`)
  return r.ok ? r.data : null
}
export const addTeamMembers = (token: string, teamId: string, agentIds: string[]) => call(token, 'POST', `/times/${teamId}/membros`, { agentIds })
export const removeTeamMember = (token: string, teamId: string, agentId: string) => call(token, 'DELETE', `/times/${teamId}/membros/${agentId}`)
export async function listTeamFallbackAgents(token: string, teamId: string): Promise<TeamPerson[] | null> {
  const r = await call(token, 'GET', `/times/${teamId}/fallback`)
  return r.ok ? r.data : null
}
export const addTeamFallbackAgents = (token: string, teamId: string, agentIds: string[]) => call(token, 'POST', `/times/${teamId}/fallback`, { agentIds })
export const removeTeamFallbackAgent = (token: string, teamId: string, agentId: string) => call(token, 'DELETE', `/times/${teamId}/fallback/${agentId}`)
export const deleteTeam = (token: string, teamId: string) => call(token, 'DELETE', `/times/${teamId}`)
export async function listExternalUnits(token: string, uf: string): Promise<{ ok: boolean; problem: string | null; units: ExternalUnitOption[] }> {
  const r = await call(token, 'GET', `/times/unidades-externas?uf=${encodeURIComponent(uf)}`)
  return r.ok && r.data ? r.data : { ok: false, problem: 'Não deu pra buscar as unidades.', units: [] }
}
/** Vincula (idUnidade) ou desvincula (null) a unidade ISBET. 409 = a unidade já é de outro time (data.problem). */
export const linkTeamExternalUnit = (token: string, teamId: string, idUnidade: number | null, municipio: string | null) =>
  call(token, 'PUT', `/times/${teamId}/unidade-externa`, { idUnidade, municipio })
export async function createTeamFull(token: string, data: { unidadeId: string; nome: string; uf?: string; isFallback?: boolean }) {
  return call(token, 'POST', '/times', data)
}
